import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  Note,
  NoteInput,
  NoteList,
  NoteReview,
  NOTE_METHODS,
  NOTE_MAX_BRANCHES,
  NOTE_SECTIONS,
  NoteAssetTicket,
  NoteAssetView,
  type NoteMethod,
  type StudyStart,
} from "@ca/shared";
import {
  Button,
  Chip,
  ErrorState,
  Fish,
  Modal,
  PageHead,
  Skeleton,
  SpaceTag,
  Switch,
} from "@ca/ui";
import { Link } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { NewBadge } from "./flags.tsx";
import { NB_STEPS, NB_THUMB, NB_TITLE_PH, NB_WHEN } from "./notebook-art.ts";
import { useNoteTotal } from "./side-counts.ts";
import { api } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import {
  NoteActions,
  NoteStore,
  toInput,
  sanitizeLocalNote,
  type NoteDraft,
} from "./note-store.ts";
import { pageMeta } from "./navigation.ts";
import { useWrite } from "./revision-ui.tsx";
import { useRevisionStudy } from "./use-study.ts";
const store = new NoteStore();
type Filter = "all" | "pin" | "nb" | "test" | "quick" | "self";
const FILTERS: [Filter, string][] = [
  ["all", "Tất cả"],
  ["pin", "📌 Ghi chú ghim"],
  ["nb", "📒 Trang sổ"],
  ["test", "📝 Ghi lúc làm bài"],
  ["quick", "⚡ Ghi nhanh"],
  ["self", "✍️ Tự viết"],
];
/** Nhãn màu của bản mẫu (`NB_TAGS`), lưu bằng tên nhãn. */
const NB_TAGS: [string, string][] = [
  ["Quan trọng", "#E5484D"],
  ["Ôn trước thi", "#E0801F"],
  ["Từ vựng", "#C29B00"],
  ["Ngữ pháp", "#2F9E66"],
  ["Mẹo làm bài", "#3E8ED0"],
  ["Bẫy hay gặp", "#8E4EC6"],
];
const BOOK_METHODS = ["cornell", "box", "map", "mind", "plain"] as const;
const NB_ICON: Record<NoteMethod, string> = {
  quick: "🗒️",
  cornell: "📑",
  box: "🔲",
  map: "🗺️",
  mind: "🧠",
  plain: "📓",
};
const TITLES: Record<NoteMethod, string> = {
  quick: "Ghi nhanh",
  cornell: "Phương pháp Cornell",
  box: "Phương pháp đóng hộp",
  map: "Phương pháp bản đồ",
  mind: "Phương pháp sơ đồ tư duy",
  plain: "Quyển vở bình thường",
};
const THUMB_BG: Record<string, string> = {
  cornell: "kem",
  box: "tim",
  map: "xanh",
  mind: "la",
  plain: "hong",
};
/** Màu giấy của app ↔ lớp màu giấy của bản mẫu. */
const PAPER_TONE: Record<string, string> = {
  white: "kem",
  yellow: "vang",
  pink: "hong",
  blue: "xanh",
  green: "la",
  lavender: "tim",
};
function excerpt(note: Note) {
  const text = [note.doc.html, ...note.doc.sections].join(" ");
  return new DOMParser()
    .parseFromString(text, "text/html")
    .body.textContent.replace(/\s+/g, " ")
    .trim()
    .slice(0, 240);
}
const shortDate = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const actions = new NoteActions(sessionStorage);
/** Ghim/bỏ ghim trên server; không đổi `rev` nên không đụng bản nháp đang soạn. */
async function setPin(uid: string, id: string, pinned: boolean) {
  const note = Note.parse(
    await api(`/v1/me/notes/${id}/pin`, {
      method: "PUT",
      accountId: uid,
      key: uuidv7(),
      body: { pinned },
    }),
  );
  await store.cache(uid, note);
  return note;
}
const LABELS: Record<NoteMethod, string> = {
  quick: "Ghi nhanh",
  cornell: "Cornell",
  box: "Đóng hộp",
  map: "Bản đồ",
  mind: "Sơ đồ tư duy",
  plain: "Vở bình thường",
};
let browserWriter: Promise<string> | null = null;
function writerId(): Promise<string> {
  if (browserWriter) return browserWriter;
  const key = "ca-note-writer",
    token = sessionStorage.getItem(key) ?? uuidv7();
  sessionStorage.setItem(key, token);
  browserWriter = new Promise<string>((resolve) => {
    if (!("locks" in navigator)) {
      resolve(token);
      return;
    }
    void navigator.locks
      .request(`ca-note-writer:${token}`, { ifAvailable: true }, async (lock) => {
        if (!lock) {
          const fresh = uuidv7();
          sessionStorage.setItem(key, fresh);
          resolve(fresh);
          return;
        }
        resolve(token);
        // Browsers release this lease on navigation or window close.
        await new Promise<void>(() => {
          /* Lifetime of the current document. */
        });
      })
      .catch(() => {
        resolve(token);
      });
  });
  return browserWriter;
}
function newInput(
  method: NoteMethod,
  context: NoteInput["context"] = null,
  attemptId: string | null = null,
) {
  return NoteInput.parse({
    rev: 0,
    method,
    title: "",
    doc: {},
    context,
    attemptId,
    tags: context ? [context.label.slice(0, 40)] : [],
  });
}
function noteActivity(context: NoteInput["context"]): StudyStart["activity"] {
  if (context?.activity) return context.activity;
  const path = context?.path ?? "";
  return path.startsWith("/grammar")
    ? "gram"
    : path.startsWith("/vocab")
      ? "vocab"
      : path.startsWith("/errors")
        ? "fix"
        : path.startsWith("/dictation")
          ? "dict"
          : path.startsWith("/practice/")
            ? "part"
            : path.startsWith("/attempts/")
              ? "test"
              : "other";
}
export function NoteRuntime({ uid, notify }: { uid: string; notify: (s: string) => void }) {
  const client = useQueryClient();
  const [quick, setQuick] = useState<{ id: string; body: NoteInput } | null>(null);
  useEffect(() => {
    const sync = () => {
      void store
        .drain(uid)
        .then(() => {
          window.dispatchEvent(new CustomEvent("ca-note-synced", { detail: uid }));
          void client.invalidateQueries({ queryKey: ["notes", uid] });
        })
        .catch(() => {});
    };
    const timer = setInterval(sync, 5000);
    window.addEventListener("online", sync);
    sync();
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", sync);
    };
  }, [uid, client]);
  useEffect(() => {
    const saveBlog = (e: Event) => {
      const detail: unknown = (e as CustomEvent<unknown>).detail;
      if (typeof detail !== "string") return;
      void actions
        .create(uid, `blog:${detail}`, (id) => ({
          path: `/v1/me/notes/blog/${id}`,
          body: { slug: detail },
        }))
        .then(() => {
          notify("Đã lưu bài blog vào ghi chú.");
        })
        .catch((e: unknown) => {
          notify(e instanceof Error ? e.message : "Chưa lưu được.");
        });
    };
    window.addEventListener("ca-note-blog", saveBlog);
    return () => {
      window.removeEventListener("ca-note-blog", saveBlog);
    };
  }, [uid, notify]);
  useEffect(() => {
    // Nút "🗒️ Ghi nhanh" trên thanh trên cùng (và mục Ghi nhanh ở menu Take note) mở khung này.
    const open = () => {
      const meta = pageMeta(window.location.pathname),
        current = window.location.pathname,
        match = /^\/attempts\/([\da-f-]{36})$/i.exec(current),
        question = document
          .querySelector("[data-note-question]")
          ?.getAttribute("data-note-question");
      setQuick({
        id: uuidv7(),
        body: newInput(
          "quick",
          { path: current, label: meta.label, ...(question ? { question } : {}) },
          match?.[1] ?? null,
        ),
      });
    };
    window.addEventListener("ca-note-open", open);
    return () => {
      window.removeEventListener("ca-note-open", open);
    };
  }, []);
  useEffect(() => {
    if (!quick) return;
    const previous = document.activeElement;
    const timer = setTimeout(() => {
      document.querySelector<HTMLElement>(".notepanel button")?.focus();
    }, 0);
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Escape") setQuick(null);
      if (e.key === "Tab") {
        const items = [
          ...document.querySelectorAll<HTMLElement>(
            ".notepanel button:not(:disabled), .notepanel input:not(:disabled), .notepanel [contenteditable], .notepanel a",
          ),
        ];
        const first = items[0],
          last = items.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener("keydown", listener);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", listener);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [quick]);
  return (
    <>
      {quick &&
        createPortal(
          <QuickPanel
            key={quick.id}
            uid={uid}
            id={quick.id}
            initial={quick.body}
            notify={notify}
            close={() => {
              setQuick(null);
            }}
          />,
          document.body,
        )}
    </>
  );
}
const QUICK_TONES: [NoteInput["color"], string, string][] = [
  ["blue", "var(--t-blue)", "xanh"],
  ["yellow", "var(--t-yellow)", "vàng"],
  ["pink", "var(--t-pink)", "hồng"],
  ["green", "var(--t-sage)", "xanh lá"],
];
/** Khung "🗒️ Ghi nhanh" trượt từ phải (`openTakeNote` của bản mẫu). */
function QuickPanel({
  uid,
  id,
  initial,
  notify,
  close,
}: {
  uid: string;
  id: string;
  initial: NoteInput;
  notify: (s: string) => void;
  close: () => void;
}) {
  const client = useQueryClient();
  const [body, setBody] = useState<NoteInput>({
      ...initial,
      title: initial.title || initial.context?.label || "Ghi nhanh",
      color: initial.color === "white" ? "blue" : initial.color,
    }),
    [busy, setBusy] = useState(false);
  const latest = useRef(body);
  latest.current = body;
  useRevisionStudy(uid, "note", undefined, noteActivity(body.context));
  const command = (cmd: string, value?: string) => {
    editCommand(cmd, false, value);
    document.activeElement?.dispatchEvent(new Event("input", { bubbles: true }));
  };
  const save = async () => {
    setBusy(true);
    try {
      const writer = await writerId();
      await store.put(uid, id, writer, latest.current);
      await store.drain(uid);
      await client.invalidateQueries({ queryKey: ["notes", uid] });
      notify("Đã lưu vào Take note.");
      close();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Đã giữ bản nháp trên máy.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    document.querySelector<HTMLElement>(".notepanel .np-body")?.focus();
  }, []);
  return (
    <aside className="notepanel" role="dialog" aria-modal="true" aria-label="Ghi nhanh">
      <header>
        <div className="grow">
          <b style={{ fontSize: 16 }}>🗒️ Ghi nhanh</b>
          <div className="faint" style={{ fontSize: 12, fontWeight: 700 }}>
            ghi tại: {body.context?.label ?? "Take note"}
            {body.context?.question ? ` · ${body.context.question}` : ""}
          </div>
        </div>
        <button className="xbtn" aria-label="Đóng Ghi nhanh" onClick={close}>
          ✕
        </button>
      </header>
      <div className="tn-tools" role="toolbar" aria-label="Màu và định dạng">
        {QUICK_TONES.map(([value, color, name]) => (
          <button
            key={value}
            className="tn-dot"
            style={{ background: color }}
            aria-pressed={body.color === value}
            aria-label={`Giấy màu ${name}`}
            onClick={() => {
              setBody({ ...latest.current, color: value });
            }}
          />
        ))}
        <span className="tn-sep" />
        <button
          className="tn-fb"
          aria-label="In đậm"
          onMouseDown={(e) => {
            e.preventDefault();
            command("bold");
          }}
        >
          <b>B</b>
        </button>
        <button
          className="tn-fb"
          aria-label="Highlight"
          onMouseDown={(e) => {
            e.preventDefault();
            command("hiliteColor", "#FFE38A");
          }}
        >
          🖍
        </button>
        <button
          className="tn-fb"
          aria-label="Bullet points"
          onMouseDown={(e) => {
            e.preventDefault();
            command("insertUnorderedList");
          }}
        >
          •
        </button>
      </div>
      <input
        className="tn-title"
        aria-label="Tiêu đề ghi chú"
        maxLength={200}
        value={body.title}
        onChange={(e) => {
          setBody({ ...latest.current, title: e.currentTarget.value });
        }}
      />
      <RichField
        className="np-body"
        label="Nội dung"
        placeholder="Ghi nhanh: quy tắc, từ mới, câu hay sai…"
        value={body.doc.html}
        change={(html) => {
          setBody({ ...latest.current, doc: { ...latest.current.doc, html } });
        }}
      />
      <div className="tn-foot">
        <span className="faint" style={{ fontSize: 12, fontWeight: 650 }}>
          Đang viết: tính giờ vào nhóm “Khác”, đóng là dừng.
        </span>
        <Button
          variant="acc"
          disabled={busy}
          onClick={() => {
            void save();
          }}
        >
          {busy ? "Đang lưu…" : "Lưu vào Take note"}
        </Button>
      </div>
    </aside>
  );
}
function editCommand(command: string, showUI: boolean, value?: string) {
  // Chromium's native editing commands preserve its undo history; Range mutations do not.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  return document.execCommand(command, showUI, value);
}
function RichField({
  value,
  label,
  change,
  className = "nb-ed",
  placeholder,
}: {
  value: string;
  label: string;
  change: (html: string) => void;
  className?: string;
  placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement>(null),
    changeRef = useRef(change);
  changeRef.current = change;
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== value)
      ref.current.innerHTML = sanitizeLocalNote(value);
  }, [value]);
  return (
    <div
      className={className}
      data-ph={placeholder}
      spellCheck={false}
      ref={ref}
      role="textbox"
      aria-label={label}
      aria-multiline="true"
      contentEditable
      suppressContentEditableWarning
      onInput={(e) => {
        changeRef.current(e.currentTarget.innerHTML);
      }}
      onPaste={(e) => {
        e.preventDefault();
        const html = e.clipboardData.getData("text/html"),
          plain = e.clipboardData.getData("text/plain");
        if (html) editCommand("insertHTML", false, sanitizeLocalNote(html));
        else editCommand("insertText", false, plain);
        if (ref.current) changeRef.current(ref.current.innerHTML);
      }}
      onKeyDown={(e) => {
        if (e.key !== ">") return;
        const selection = window.getSelection();
        if (
          !selection?.isCollapsed ||
          !selection.anchorNode ||
          selection.anchorNode.nodeType !== Node.TEXT_NODE
        )
          return;
        const text = selection.anchorNode.textContent ?? "",
          pos = selection.anchorOffset;
        if (pos > 0 && text[pos - 1] === "-") {
          e.preventDefault();
          const range = document.createRange();
          range.setStart(selection.anchorNode, pos - 1);
          range.setEnd(selection.anchorNode, pos);
          selection.removeAllRanges();
          selection.addRange(range);
          editCommand("insertText", false, "→");
          if (ref.current) changeRef.current(ref.current.innerHTML);
        }
      }}
    />
  );
}
const WEEKDAY = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
/* Font, giấy, màu giấy của app hiện bằng tên và kiểu của bản mẫu (dữ liệu giữ nguyên giá trị cũ). */
const FONTS: [NoteInput["font"], string, string, string][] = [
  ["sans", "nunito", "Nunito", "tròn, dễ đọc"],
  ["serif", "quicksand", "Quicksand", "mảnh, thoáng"],
  ["mono", "baloo", "Baloo 2", "đậm, vui"],
];
const FONT_CLASS: Record<string, string> = { sans: "nunito", serif: "quicksand", mono: "baloo" };
const PAPERS: [NoteInput["paper"], string, string][] = [
  ["lined", "ngang", "Kẻ ngang"],
  ["dots", "soc", "Kẻ sọc"],
  ["grid", "o", "Kẻ ô"],
  ["blank", "tron", "Trơn"],
];
const PAPER_CLASS: Record<string, string> = {
  lined: "ngang",
  dots: "soc",
  grid: "o",
  blank: "tron",
};
const COLORS: [NoteInput["color"], string, string, string, string][] = [
  ["white", "kem", "kem", "#FBF7EE", "#E8DCC4"],
  ["pink", "hong", "hồng", "#FCEBF2", "#F1C6D7"],
  ["blue", "xanh", "xanh dương", "#EAF1FB", "#BFD3EE"],
  ["green", "la", "xanh lá", "#EDF4E7", "#C5D9B5"],
  ["yellow", "vang", "vàng", "#FBF6DA", "#EBDD94"],
  ["lavender", "tim", "tím", "#F0EDFD", "#CFC5F5"],
];
const HIGHLIGHTS: [string, string][] = [
  ["#FFE38A", "vàng"],
  ["#FFC9DD", "hồng"],
  ["#C8EFB4", "xanh lá"],
  ["#BEE0FF", "xanh dương"],
  ["#DCD1FF", "tím"],
];
const SYMBOLS: [string, string][] = [
  ["→", "dẫn tới"],
  ["⇒", "suy ra"],
  ["≈", "đồng nghĩa · paraphrase"],
  ["≠", "khác · trái nghĩa"],
  ["✓", "đúng"],
  ["✗", "sai"],
  ["⚠", "bẫy"],
  ["★", "quan trọng"],
  ["❓", "chưa hiểu"],
  ["🔑", "từ khoá"],
  ["💡", "mẹo"],
  ["⏰", "chú ý thời gian"],
];
type StickerText = "⭐" | "🐟" | "💡" | "📌" | "✅" | "⚠️";
const STICKERS: [StickerText, string][] = [
  ["⭐", "quan trọng"],
  ["💡", "mẹo"],
  ["📌", "ghi nhớ"],
  ["✅", "đã hiểu"],
  ["⚠️", "bẫy"],
  ["🐟", "Cá"],
];
const TONES = ["lav", "pink", "sage", "yellow", "blue", "peach"];
function withExample(body: NoteInput): NoteInput {
  const sample = [
    "<p>Which word signals the tense?</p>",
    "<p><b>By next Friday</b>, the team <mark>will have completed</mark> the report.</p>",
    "<p>By + thời điểm tương lai → future perfect.</p>",
  ];
  const free = body.method === "plain" || body.method === "quick";
  return {
    ...body,
    title: body.title || "Future perfect · TOEIC",
    doc: {
      ...body.doc,
      html: free ? sample.join("") : "",
      sections: free
        ? []
        : body.method === "cornell"
          ? sample
          : [
              "<p>Future perfect</p>",
              sample[1] ?? "",
              sample[2] ?? "",
              "<p>So sánh với future simple.</p>",
            ],
    },
  };
}
/** Sticker dán trên tờ giấy: kéo để dời, phím mũi tên để dịch, Delete để bỏ. */
function Sticker({
  sticker,
  move,
  remove,
}: {
  sticker: { text: string; x: number; y: number };
  move: (x: number, y: number) => void;
  remove: () => void;
}) {
  const clamp = (v: number) => Math.max(0, Math.min(95, v));
  return (
    <button
      className="note-sticker"
      style={{ left: `${sticker.x}%`, top: `${sticker.y}%` }}
      aria-label={`Sticker ${sticker.text}, kéo để di chuyển; Delete để xoá`}
      onKeyDown={(e) => {
        if (e.key === "Delete" || e.key === "Backspace") remove();
        const dx = e.key === "ArrowLeft" ? -2 : e.key === "ArrowRight" ? 2 : 0,
          dy = e.key === "ArrowUp" ? -2 : e.key === "ArrowDown" ? 2 : 0;
        if (dx || dy) {
          e.preventDefault();
          move(clamp(sticker.x + dx), clamp(sticker.y + dy));
        }
      }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        const rect = e.currentTarget.parentElement?.getBoundingClientRect();
        if (!rect) return;
        move(
          clamp(((e.clientX - rect.left) / rect.width) * 100),
          clamp(((e.clientY - rect.top) / rect.height) * 100),
        );
      }}
    >
      {sticker.text}
    </button>
  );
}
async function resizeImage(file: File) {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type) || file.size > 20 * 1024 * 1024)
    throw new Error("Chọn ảnh PNG/JPEG/WebP tối đa 20 MB.");
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height)),
    canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error("Không xử lý được ảnh."));
      },
      file.type === "image/png" ? "image/png" : "image/jpeg",
      0.85,
    );
  });
  if (blob.size > 3 * 1024 * 1024) throw new Error("Ảnh sau thu nhỏ vẫn vượt 3 MB.");
  return blob;
}
function PrivateImage({ uid, id, remove }: { uid: string; id: string; remove?: () => void }) {
  const query = useQuery({
    queryKey: ["notes", uid, "asset", id],
    queryFn: async () =>
      NoteAssetView.parse(await api(`/v1/me/notes/assets/${id}`, { accountId: uid })),
    refetchInterval: 240000,
  });
  return (
    <figure>
      {query.data ? (
        <img src={query.data.url} alt="Ảnh trong ghi chú" referrerPolicy="no-referrer" />
      ) : (
        <p>{query.error ? "Chưa đọc được ảnh riêng tư." : "Đang tải ảnh…"}</p>
      )}
      {remove && <button onClick={remove}>Bỏ ảnh</button>}
    </figure>
  );
}
function NoteEditor({
  uid,
  id,
  initial,
  notify,
  close,
  onNew,
  writerToken,
  pinnedAt = null,
}: {
  uid: string;
  id: string;
  initial: NoteInput;
  pinnedAt?: string | null;
  writerToken?: string | undefined;
  notify: (s: string) => void;
  close?: () => void;
  onNew?: () => void;
}) {
  const navigate = useNavigate();
  const [tagOpen, setTagOpen] = useState(false),
    [remove, setRemove] = useState(false),
    [pop, setPop] = useState<"sym" | "stk" | null>(null),
    [hl, setHl] = useState("#FFE38A");
  const siblings = useQuery({
    queryKey: ["notes", uid, "list", `method=${initial.method}&siblings`],
    queryFn: async () =>
      NoteList.parse(
        await api(`/v1/me/notes?q=&offset=0&limit=30&method=${initial.method}`, { accountId: uid }),
      ),
  });
  const [writer, setWriter] = useState(writerToken ?? "");
  const key = `${uid}:${id}:${writer}`,
    client = useQueryClient();
  const [body, setBody] = useState(initial),
    [draft, setDraft] = useState<NoteDraft | null>(null),
    [ready, setReady] = useState(false),
    [localError, setLocalError] = useState(""),
    [busy, setBusy] = useState(false),
    [pinned, setPinned] = useState(!!pinnedAt);
  const latest = useRef(body),
    pending = useRef<Promise<unknown>>(Promise.resolve()),
    hydrated = useRef(false);
  latest.current = body;
  const write = useWrite(uid, notify);
  useRevisionStudy(uid, "note", body.rev > 0 ? id : undefined, noteActivity(body.context));
  const update = (next: NoteInput) => {
    latest.current = next;
    setBody(next);
    pending.current = pending.current
      .then(async () => {
        const saved = await store.put(uid, id, writer, next);
        setDraft(saved);
        setLocalError("");
      })
      .catch((e: unknown) => {
        setLocalError(e instanceof Error ? e.message : "Chưa lưu được bản nháp. Giữ trang này mở.");
      });
  };
  useEffect(() => {
    if (!writer) {
      void writerId().then(setWriter);
      return;
    }
    void store.get(key).then((saved) => {
      if (saved && (saved.dirty || saved.body.rev >= initial.rev)) {
        setDraft(saved);
        setBody(saved.body);
        latest.current = saved.body;
      } else if (saved) {
        void store.remove(key);
      }
      hydrated.current = true;
      setReady(true);
    });
  }, [key, writer]);
  useEffect(() => {
    const sync = () => {
      void pending.current
        .then(() => store.get(key))
        .then((saved) => {
          if (!saved) return;
          setDraft(saved);
          if (saved.body.rev > latest.current.rev) {
            const next = { ...latest.current, rev: saved.body.rev, context: saved.body.context };
            latest.current = next;
            setBody(next);
          }
        });
    };
    window.addEventListener("ca-note-synced", sync);
    return () => {
      window.removeEventListener("ca-note-synced", sync);
    };
  }, [key]);
  useEffect(() => {
    const unload = (e: BeforeUnloadEvent) => {
      if (localError) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", unload);
    return () => {
      window.removeEventListener("beforeunload", unload);
    };
  }, [localError]);
  async function save() {
    setBusy(true);
    try {
      if (!draft && hydrated.current) update(latest.current);
      await pending.current;
      if (localError) return;
      await store.drain(uid);
      const saved = await store.get(key);
      if (saved) {
        setDraft(saved);
        setBody(saved.body);
        latest.current = saved.body;
      }
      await client.invalidateQueries({ queryKey: ["notes", uid] });
      if (saved?.blocked) notify(saved.error ?? "Bản nháp chưa đồng bộ.");
      else if (saved?.dirty) notify("Đã giữ bản nháp trên máy. Sẽ thử đồng bộ khi có mạng.");
      else {
        notify("Đã lưu ghi chú.");
        const all = NoteList.parse(await api("/v1/me/notes?limit=1", { accountId: uid }));
        if (all.limit !== null && all.total >= all.limit)
          notify("Đã lưu trang này. Kho Free đã đủ 15 ghi chú; nâng cấp để tạo thêm.");
      }
    } catch (e) {
      notify(e instanceof Error ? e.message : "Đã giữ bản nháp trên máy.");
    } finally {
      setBusy(false);
    }
  }
  async function upload(file: File) {
    if (busy) return;
    setBusy(true);
    try {
      update(latest.current);
      await pending.current;
      await store.drain(uid);
      const synced = await store.get(key);
      if (!synced || synced.dirty || synced.blocked || synced.body.rev === 0)
        throw new Error("Cần đồng bộ trang trước khi tải ảnh.");
      latest.current = synced.body;
      setBody(synced.body);
      setDraft(synced);
      const image = await resizeImage(file),
        assetId = uuidv7();
      const ticket = NoteAssetTicket.parse(
        await api("/v1/me/notes/assets", {
          method: "POST",
          accountId: uid,
          body: { id: assetId, noteId: id, mime: image.type, bytes: image.size },
        }),
      );
      const uploaded = await fetch(ticket.url, {
        method: "PUT",
        headers: { "Content-Type": image.type },
        body: image,
      });
      if (!uploaded.ok) throw new Error("Không tải được ảnh. Thử lại.");
      await api(`/v1/me/notes/assets/${assetId}/confirm`, { method: "POST", accountId: uid });
      update({
        ...latest.current,
        doc: { ...latest.current.doc, assets: [...latest.current.doc.assets, assetId] },
      });
    } catch (e) {
      notify(e instanceof Error ? e.message : "Chưa tải được ảnh.");
    } finally {
      setBusy(false);
    }
  }
  if (!ready) return <Skeleton />;
  const m = body.method;
  const sectionLabels = NOTE_SECTIONS[m];
  const section = (i: number) =>
    m === "plain" || m === "quick"
      ? body.doc.html
      : (body.doc.sections[i] ?? (i === 1 ? body.doc.html : ""));
  const setSection = (i: number) => (html: string) => {
    if (m === "plain" || m === "quick")
      update({ ...latest.current, doc: { ...latest.current.doc, html } });
    else {
      const sections = [...latest.current.doc.sections];
      while (sections.length < Math.max(sectionLabels.length, i + 1)) sections.push("");
      sections[i] = html;
      update({ ...latest.current, doc: { ...latest.current.doc, html: "", sections } });
    }
  };
  // Bản đồ / sơ đồ tư duy: số nhánh theo dữ liệu (tối thiểu như mẫu), tên nhánh do người học đặt.
  const branches = Math.max(sectionLabels.length - 1, body.doc.sections.length - 1, 1);
  const branchName = (i: number) => body.doc.labels[i] ?? sectionLabels[i] ?? "";
  const writeBranches = (sections: string[], labels: string[]) => {
    update({ ...latest.current, doc: { ...latest.current.doc, html: "", sections, labels } });
  };
  const padded = () => {
    const sections = [...latest.current.doc.sections],
      labels = [...latest.current.doc.labels];
    while (sections.length < branches + 1) sections.push("");
    while (labels.length < sections.length) labels.push(sectionLabels[labels.length] ?? "");
    return { sections, labels };
  };
  const renameBranch = (i: number, name: string) => {
    const { sections, labels } = padded();
    labels[i] = name;
    writeBranches(sections, labels);
  };
  const addBranch = () => {
    const { sections, labels } = padded();
    if (sections.length > NOTE_MAX_BRANCHES) return;
    writeBranches([...sections, ""], [...labels, ""]);
  };
  const removeBranch = (i: number) => {
    const { sections, labels } = padded();
    if (sections.length <= 2) return;
    writeBranches(
      sections.filter((_, j) => j !== i),
      labels.filter((_, j) => j !== i),
    );
  };
  const branchHead = (i: number, kind: "mp" | "mm") => {
    const noun = kind === "mp" ? "ý lớn" : "nhánh";
    return (
      <div className={`${kind}-node-wrap`}>
        <input
          className={`${kind}-node`}
          aria-label={`Tên ${noun} ${i}`}
          placeholder={kind === "mp" ? "Ý lớn" : "Nhánh"}
          value={branchName(i)}
          maxLength={200}
          size={kind === "mm" ? Math.max(6, branchName(i).length + 1) : undefined}
          style={kind === "mp" ? { width: "100%" } : undefined}
          onChange={(e) => {
            renameBranch(i, e.currentTarget.value);
          }}
        />
        {branches > 1 && (
          <button
            className="nb-mini-x"
            aria-label={`Xoá ${noun}: ${branchName(i) || `${noun} ${i}`}`}
            onClick={() => {
              removeBranch(i);
            }}
          >
            ✕
          </button>
        )}
      </div>
    );
  };
  const field = (i: number, className: string, ph: string) => (
    <RichField
      className={className}
      label={(m === "map" || m === "mind" ? branchName(i) : sectionLabels[i]) || ph}
      placeholder={ph}
      value={section(i)}
      change={setSection(i)}
    />
  );
  const command = (cmd: string, value?: string) => {
    editCommand(cmd, false, value);
    document.activeElement?.dispatchEvent(new Event("input", { bubbles: true }));
  };
  const keep = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    fn();
  };
  const status =
    localError ||
    (draft?.blocked
      ? "Cần xử lý bản nháp"
      : draft?.dirty
        ? "Đã giữ bản nháp trên máy"
        : body.rev
          ? "✓ Tự lưu · đã đồng bộ"
          : "Trang mới");
  const today = new Date();
  const meta = `${WEEKDAY[today.getDay()]} ${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()} · ${TITLES[m]}`;
  return (
    <>
      <section className="page-head nb-phead">
        <div className="row">
          <SpaceTag space="note" />
          <Chip>
            {NB_ICON[m]} {LABELS[m]}
          </Chip>
          <NewBadge />
        </div>
        <h1>{TITLES[m]}</h1>
        <p>{NB_WHEN[m] ?? "Ghi nhanh ở bất kỳ trang nào, gom về khu Take note."}</p>
        {NB_STEPS[m] && (
          <ol className="nb-steps">
            {NB_STEPS[m].map((s, i) => (
              <li key={s}>
                <b>{i + 1}</b>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        )}
      </section>
      <section className="nb-card" data-m={m}>
        <div className="nb-bar">
          <div className="nb-pages" role="tablist" aria-label={`Các trang ${LABELS[m]}`}>
            {(siblings.data?.items.length ? siblings.data.items : []).map((n, i) => (
              <button
                key={n.id}
                className="nb-pg"
                role="tab"
                aria-selected={n.id === id}
                title={n.title || `Trang ${i + 1}`}
                onClick={() => {
                  if (n.id !== id) void navigate({ to: "/$", params: { _splat: `notes/${n.id}` } });
                }}
              >
                {n.title || `Trang ${i + 1}`}
              </button>
            ))}
            {!siblings.data?.items.some((n) => n.id === id) && (
              <button className="nb-pg" role="tab" aria-selected="true">
                {body.title || "Trang mới"}
              </button>
            )}
          </div>
          {m !== "quick" && (
            <Button
              variant="soft"
              size="sm"
              onClick={() => {
                onNew?.();
              }}
            >
              ＋ Trang mới
            </Button>
          )}
          <Button
            variant="acc"
            size="sm"
            data-tip="Điền ví dụ TOEIC|Trang đang trống thì điền ví dụ vào trang này"
            onClick={() => {
              if (body.doc.html || body.doc.sections.some(Boolean)) {
                if (!confirm("Thay nội dung chữ bằng ví dụ TOEIC?")) return;
              }
              update(withExample(body));
            }}
          >
            ✨ Điền ví dụ TOEIC
          </Button>
          <span className="nb-pacts">
            <Button
              variant="soft"
              size="sm"
              className={`nb-pinbtn${pinned ? " on" : ""}`}
              aria-pressed={pinned}
              disabled={body.rev === 0}
              data-tip={
                body.rev === 0
                  ? "Ghim trang|Viết vài chữ để trang được lưu rồi ghim"
                  : pinned
                    ? "Bỏ ghim|Trang này đang nằm trong tab Ghi chú ghim"
                    : "Ghim trang này|Trang ghim nằm trong tab Ghi chú ghim"
              }
              onClick={() => {
                const next = !pinned;
                setPinned(next);
                void setPin(uid, id, next)
                  .then(() => client.invalidateQueries({ queryKey: ["notes", uid] }))
                  .catch((e: unknown) => {
                    setPinned(!next);
                    notify(e instanceof Error ? e.message : "Chưa ghim được.");
                  });
              }}
            >
              📌 {pinned ? "Đã ghim" : "Ghim"}
            </Button>
            <Button
              variant="soft"
              size="sm"
              data-tip="Nhãn màu|Gắn nhãn để lọc ở trang Tất cả ghi chú"
              onClick={() => {
                setTagOpen(true);
              }}
            >
              🏷 Nhãn
            </Button>
            {body.rev > 0 && (
              <Button
                variant="soft"
                size="sm"
                disabled={busy}
                data-tip="Xoá trang này|Sẽ hỏi lại trước khi xoá vĩnh viễn"
                onClick={() => {
                  setRemove(true);
                }}
              >
                🗑 Xoá trang
              </Button>
            )}
          </span>
          <span className="grow" />
          <span
            className={`nb-sess${body.rev > 0 ? "" : " paused"}`}
            data-tip="Giờ ghi chép|Mở trang chưa tính. Bắt đầu viết mới tính vào nhóm “Khác”; rời trang là dừng."
          >
            <span className="ss-dot" />
            <span className="ss-lbl">
              {body.rev > 0 ? "đang tính giờ khi bạn viết" : "chưa tính giờ"}
            </span>
          </span>
          <span className={`nb-saved${draft?.dirty ? " ing" : ""}`} role="status">
            {status}
          </span>
          <Button
            variant="dark"
            size="sm"
            disabled={busy || !!localError}
            onClick={() => {
              void save();
            }}
          >
            {busy ? "Đang lưu…" : "Lưu ghi chú"}
          </Button>
          {close && (
            <Button variant="line" size="sm" onClick={close}>
              Đóng, giữ bản nháp
            </Button>
          )}
        </div>
        {draft?.error && (
          <div className="resume" role="alert">
            <b>{draft.error}</b>
            <div className="row">
              <Button
                variant="soft"
                size="sm"
                onClick={() => {
                  const blob = new Blob([JSON.stringify(latest.current, null, 2)], {
                      type: "application/json",
                    }),
                    url = URL.createObjectURL(blob),
                    a = document.createElement("a");
                  a.href = url;
                  a.download = `ghi-chu-${id}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Tải bản nháp về máy
              </Button>
              <Button
                variant="soft"
                size="sm"
                onClick={() => {
                  if (!confirm("Bỏ bản nháp local này và tải nội dung trên server?")) return;
                  void api(`/v1/me/notes/${id}`, { accountId: uid })
                    .then((value) => Note.parse(value))
                    .then(async (note) => {
                      await store.remove(key);
                      await store.cache(uid, note);
                      const next = toInput(note);
                      setBody(next);
                      latest.current = next;
                      setDraft(null);
                    })
                    .catch((e: unknown) => {
                      notify(e instanceof Error ? e.message : "Chưa tải được.");
                    });
                }}
              >
                Dùng bản trên server
              </Button>
              <Button
                variant="soft"
                size="sm"
                onClick={() => {
                  void store
                    .retry(key)
                    .then(() => store.drain(uid))
                    .then(() => {
                      window.dispatchEvent(new CustomEvent("ca-note-synced", { detail: uid }));
                    });
                }}
              >
                Thử đồng bộ lại
              </Button>
              <a className="btn btn-acc btn-sm" href="/pro">
                Nâng cấp Pro
              </a>
            </div>
          </div>
        )}
        <div className="nb-look">
          <div className="nb-tools" role="toolbar" aria-label="Chữ và giấy">
            <div className="nbg">
              <span className="nbg-l">Chữ</span>
              {FONTS.map(([value, cls, name, tip]) => (
                <button
                  key={value}
                  className={`nbt nbf f-${cls}`}
                  aria-pressed={body.font === value}
                  data-tip={`${name}|${tip}`}
                  onClick={() => {
                    update({ ...body, font: value });
                  }}
                >
                  Aa {name}
                </button>
              ))}
            </div>
            <div className="nbg">
              <span className="nbg-l">Giấy</span>
              {PAPERS.map(([value, cls, name]) => (
                <button
                  key={value}
                  className="nbt"
                  aria-pressed={body.paper === value}
                  aria-label={`Giấy ${name.toLowerCase()}`}
                  onClick={() => {
                    update({ ...body, paper: value });
                  }}
                >
                  <i className={`pv pv-${cls}`} />
                  <span className="nbt-l">{name}</span>
                </button>
              ))}
            </div>
            <div className="nbg">
              <span className="nbg-l">Màu giấy</span>
              {COLORS.map(([value, , name, c, c2]) => (
                <button
                  key={value}
                  className="nbdot paper"
                  aria-pressed={body.color === value}
                  style={{ "--c": c, "--c2": c2 } as CSSProperties}
                  aria-label={`Giấy màu ${name}`}
                  data-tip={`Giấy màu ${name}`}
                  onClick={() => {
                    update({ ...body, color: value });
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="nb-toolwrap">
          <div className="nb-tools" role="toolbar" aria-label="Công cụ viết">
            <div className="nbg">
              <span className="nbg-l">Công cụ</span>
              <button
                className="nbt"
                aria-pressed="true"
                data-tip="Gõ chữ|Bấm vào một ô trên trang để gõ"
              >
                <span className="nbt-i">⌨️</span>
                <span className="nbt-l">Gõ chữ</span>
              </button>
              {/* Vẽ tay đã chốt làm sau ra mắt: hiện nút nhưng khoá. */}
              <button className="nbt" disabled data-tip="Bút vẽ tay|Sẽ có sau khi ra mắt">
                <span className="nbt-i">✍️</span>
                <span className="nbt-l">Bút</span>
              </button>
              <button className="nbt" disabled data-tip="Tẩy|Sẽ có cùng bút vẽ tay">
                <span className="nbt-i">🧽</span>
                <span className="nbt-l">Tẩy</span>
              </button>
              <button
                className="nbt"
                data-tip="Hoàn tác|Lùi lại thao tác chữ vừa làm"
                onMouseDown={keep(() => {
                  command("undo");
                })}
              >
                <span className="nbt-i">↶</span>
                <span className="nbt-l">Hoàn tác</span>
              </button>
            </div>
            <div className="nbg nbg-shape">
              <span className="nbg-l">Hình</span>
              {(
                [
                  ["➜", "Mũi tên"],
                  ["◯", "Khoanh"],
                  ["⎯", "Gạch chân"],
                ] as const
              ).map(([icon, name]) => (
                <button
                  key={name}
                  className="nbt"
                  disabled
                  data-tip={`${name}|Sẽ có cùng bút vẽ tay`}
                >
                  <span className="nbt-i">{icon}</span>
                  <span className="nbt-l">{name}</span>
                </button>
              ))}
            </div>
            <div className="nbg">
              <span className="nbg-l">Chèn</span>
              <button
                className="nbt"
                data-tip="Thêm bảng|Bảng chèn vào chỗ con trỏ"
                onMouseDown={keep(() => {
                  command(
                    "insertHTML",
                    "<table><tbody><tr><th>Cấu trúc</th><th>Ví dụ</th></tr><tr><td>…</td><td>…</td></tr></tbody></table><p><br></p>",
                  );
                })}
              >
                <span className="nbt-i">▦</span>
                <span className="nbt-l">Bảng</span>
              </button>
              <label className="nbt" data-tip="Thêm ảnh|Chọn ảnh từ máy, tự thu nhỏ còn 1.200px">
                <span className="nbt-i">🖼</span>
                <span className="nbt-l">Ảnh</span>
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const file = e.currentTarget.files?.[0];
                    if (file) void upload(file);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
              <button
                className="nbt"
                aria-expanded={pop === "stk"}
                data-tip="Sticker|Dán lên trang, kéo để dời chỗ"
                onClick={() => {
                  setPop(pop === "stk" ? null : "stk");
                }}
              >
                <span className="nbt-i">🟨</span>
                <span className="nbt-l">Sticker</span>
              </button>
              <button
                className="nbt"
                aria-expanded={pop === "sym"}
                data-tip="Ký hiệu và icon|→ ≈ ≠ ✓ ✗ ⚠ ★ · gõ -> cũng ra →"
                onMouseDown={keep(() => {
                  setPop(pop === "sym" ? null : "sym");
                })}
              >
                <span className="nbt-i">Ω</span>
                <span className="nbt-l">Ký hiệu</span>
              </button>
            </div>
            <div className="nbg nbg-ctx">
              <span className="nbg-l">Định dạng</span>
              <button
                className="nbt"
                aria-label="In đậm"
                data-tip="In đậm|Chọn chữ rồi bấm"
                onMouseDown={keep(() => {
                  command("bold");
                })}
              >
                <b>B</b>
              </button>
              <button
                className="nbt"
                data-tip="Highlight|Chọn chữ rồi bấm; đổi màu ở các chấm bên cạnh"
                onMouseDown={keep(() => {
                  command("hiliteColor", hl);
                })}
              >
                <span className="nbt-i">🖍</span>
                <span className="nbt-l">Highlight</span>
              </button>
              {HIGHLIGHTS.map(([color, name]) => (
                <button
                  key={color}
                  className="nbdot hl"
                  aria-pressed={hl === color}
                  style={{ "--c": color } as CSSProperties}
                  aria-label={`Highlight màu ${name}`}
                  data-tip={`Highlight màu ${name}|Đang chọn chữ thì tô luôn màu này`}
                  onMouseDown={keep(() => {
                    setHl(color);
                    command("hiliteColor", color);
                  })}
                />
              ))}
              <button
                className="nbdot hl none"
                aria-label="Bỏ highlight"
                data-tip="Bỏ highlight|Chọn chữ rồi bấm"
                onMouseDown={keep(() => {
                  command("hiliteColor", "transparent");
                })}
              />
              <button
                className="nbt"
                data-tip="Bullet points"
                onMouseDown={keep(() => {
                  command("insertUnorderedList");
                })}
              >
                <span className="nbt-i">•</span>
                <span className="nbt-l">Bullet points</span>
              </button>
            </div>
          </div>
          {pop === "sym" && (
            <div className="nb-sympop" role="group" aria-label="Ký hiệu">
              <div className="nb-symgrid">
                {SYMBOLS.map(([sym, meaning]) => (
                  <button
                    key={sym}
                    className="nbsym"
                    aria-label={`Chèn ${sym} (${meaning})`}
                    onMouseDown={keep(() => {
                      command("insertText", sym);
                    })}
                  >
                    <b>{sym}</b>
                    <small>{meaning}</small>
                  </button>
                ))}
              </div>
              <p className="nbsym-hint">
                Gõ tắt cũng được: <b>-&gt;</b> thành →
              </p>
            </div>
          )}
          {pop === "stk" && (
            <div className="nb-sympop" role="group" aria-label="Sticker">
              <div className="nb-symgrid">
                {STICKERS.map(([text, meaning]) => (
                  <button
                    key={text}
                    className="nbsym"
                    aria-label={`Dán sticker ${text}`}
                    onClick={() => {
                      setPop(null);
                      update({
                        ...body,
                        doc: {
                          ...body.doc,
                          stickers: [...body.doc.stickers, { text, x: 75, y: 5 }].slice(0, 30),
                        },
                      });
                    }}
                  >
                    <b>{text}</b>
                    <small>{meaning}</small>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {body.context && (
          <p className="faint" style={{ fontSize: 12.5, fontWeight: 650 }}>
            Đang ghi ở: <a href={body.context.path}>{body.context.label}</a>
            {body.context.question ? ` · ${body.context.question}` : ""}
          </p>
        )}
        <div className="nb-desk">
          <div
            className={`nb-sheet f-${FONT_CLASS[body.font]} p-${PAPER_CLASS[body.paper]} pc-${PAPER_TONE[body.color]} tool-text`}
            data-m={m}
          >
            <div className="nb-content">
              <header className="nbh">
                <input
                  className="nb-title"
                  aria-label="Tiêu đề ghi chú"
                  placeholder={NB_TITLE_PH[m] ?? "Tên trang"}
                  value={body.title}
                  maxLength={200}
                  onChange={(e) => {
                    update({ ...body, title: e.currentTarget.value });
                  }}
                />
                <div className="nb-meta">{meta}</div>
              </header>
              {m === "cornell" ? (
                <>
                  <div className="cn">
                    <section className="cn-cue">
                      <div className="nb-lbl">Gợi ý · câu hỏi</div>
                      {field(0, "nb-ed", "Từ khoá, câu hỏi tự kiểm tra… (viết sau buổi học)")}
                    </section>
                    <section className="cn-main">
                      <div className="nb-lbl">Ghi chép</div>
                      {field(1, "nb-ed", "Ghi ý chính trong lúc học: quy tắc, ví dụ, bẫy hay gặp…")}
                    </section>
                  </div>
                  <section className="cn-sum">
                    <div className="nb-lbl">Tóm tắt</div>
                    {field(2, "nb-ed", "Tóm lại cả trang trong 2–3 câu bằng lời của bạn")}
                  </section>
                </>
              ) : m === "box" ? (
                <div className="bx-grid">
                  {sectionLabels.map((label, i) => (
                    <section key={label} className="bx" data-tone={TONES[i]}>
                      <div className="bx-top">
                        <div className="bx-t">{label}</div>
                      </div>
                      {field(i, "nb-ed", "Mọi ý cùng chủ đề vào chung hộp này…")}
                    </section>
                  ))}
                </div>
              ) : m === "map" ? (
                <div className="mp">
                  {field(0, "mp-root", "Chủ đề chính")}
                  <div className="mp-bs">
                    {Array.from({ length: branches }, (_, k) => (
                      <div key={k} className="mp-b" data-tone={TONES[k % TONES.length]}>
                        {branchHead(k + 1, "mp")}
                        <div className="mp-leaves">{field(k + 1, "mp-leaf", "Ý nhỏ, ví dụ")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : m === "mind" ? (
                <div className="mm">
                  {(["l", "r"] as const).map((side) => {
                    // Như bản mẫu: nhánh chẵn bên phải, nhánh lẻ bên trái.
                    const col = Array.from({ length: branches }, (_, k) => k).filter(
                      (k) => (k % 2 ? "l" : "r") === side,
                    );
                    const nodes = col.map((k) => (
                      <div
                        key={k}
                        className="mm-b"
                        data-tone={TONES[(k + 1) % TONES.length]}
                        data-side={side}
                      >
                        {branchHead(k + 1, "mm")}
                        <div className="mm-kids">{field(k + 1, "mm-kid", "ý nhỏ")}</div>
                      </div>
                    ));
                    return side === "l" ? (
                      <div key={side} className="mm-col mm-l">
                        {nodes}
                      </div>
                    ) : (
                      <Fragment key={side}>
                        {field(0, "mm-center", "Ý trung tâm")}
                        <div className="mm-col mm-r">{nodes}</div>
                      </Fragment>
                    );
                  })}
                </div>
              ) : (
                <section className="pl">
                  {field(
                    0,
                    "nb-ed",
                    "Viết gì cũng được: ý chính, từ mới, câu hay sai… Bảng, ảnh, sticker đều dùng được",
                  )}
                </section>
              )}
              {body.doc.assets.map((asset) => (
                <PrivateImage
                  key={asset}
                  uid={uid}
                  id={asset}
                  remove={() => {
                    update({
                      ...body,
                      doc: { ...body.doc, assets: body.doc.assets.filter((x) => x !== asset) },
                    });
                  }}
                />
              ))}
            </div>
            {body.doc.stickers.map((sticker, i) => (
              <Sticker
                key={i}
                sticker={sticker}
                move={(x, y) => {
                  update({
                    ...latest.current,
                    doc: {
                      ...latest.current.doc,
                      stickers: latest.current.doc.stickers.map((s, j) =>
                        j === i ? { ...s, x, y } : s,
                      ),
                    },
                  });
                }}
                remove={() => {
                  update({
                    ...body,
                    doc: { ...body.doc, stickers: body.doc.stickers.filter((_, j) => j !== i) },
                  });
                }}
              />
            ))}
          </div>
        </div>
        {(m === "map" || m === "mind") && (
          <div className="row nb-addrow">
            <Button
              variant="line"
              size="sm"
              disabled={branches >= NOTE_MAX_BRANCHES}
              onClick={addBranch}
            >
              {m === "map" ? "＋ Thêm ý lớn" : "＋ Thêm nhánh"}
            </Button>
            <span className="faint">
              {m === "map"
                ? "Mỗi ý lớn một nhánh; xoá bằng nút ✕ ở góc ý lớn"
                : "Nhánh tự chia hai bên ý trung tâm; xoá bằng nút ✕ ở góc nhánh"}
            </span>
          </div>
        )}
        {body.rev > 0 && m === "plain" && (
          <div className="row">
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 650 }}>
              Sắp trang vở này vào phương pháp (trang gốc được giữ nguyên):
            </span>
            {(["cornell", "box", "map", "mind"] as const).map((target) => (
              <Button
                key={target}
                variant="soft"
                size="sm"
                onClick={() => {
                  if (draft?.dirty) {
                    notify("Lưu trang trước khi chuyển phương pháp.");
                    return;
                  }
                  void actions
                    .create(uid, `convert:${id}:${body.rev}:${target}`, (targetId) => ({
                      path: `/v1/me/notes/${id}/convert`,
                      body: { id: targetId, method: target, rev: body.rev },
                    }))
                    .then(() => {
                      notify("Đã tạo trang mới. Trang gốc được giữ nguyên.");
                      void client.invalidateQueries({ queryKey: ["notes", uid] });
                    })
                    .catch(() => {});
                }}
              >
                {NB_ICON[target]} {LABELS[target]}
              </Button>
            ))}
          </div>
        )}
        <p className="faint nb-foot">
          ⏺ Mở trang chưa tính giờ; bắt đầu viết hoặc chèn mới tính (nhóm “Khác”). Rời trang là
          dừng. Bản nháp tự lưu trên máy và đồng bộ khi có mạng.
        </p>
      </section>
      {tagOpen && (
        <Modal
          title="Nhãn màu"
          className="sm"
          head={<SpaceTag space="note" />}
          onClose={() => {
            setTagOpen(false);
          }}
        >
          <h2>🏷 Nhãn màu</h2>
          <p className="muted" style={{ fontSize: 13.5, margin: "4px 0 14px" }}>
            Gắn nhãn để lọc nhanh ở trang Tất cả ghi chú.
          </p>
          <div className="tag-filter">
            {NB_TAGS.map(([label, color]) => {
              const on = body.tags.includes(label);
              return (
                <button
                  key={label}
                  className={`tag-f${on ? " on" : ""}`}
                  style={{ "--tc": color } as CSSProperties}
                  aria-pressed={on}
                  onClick={() => {
                    update({
                      ...body,
                      tags: on
                        ? body.tags.filter((t) => t !== label)
                        : [...body.tags, label].slice(0, 12),
                    });
                  }}
                >
                  <i />
                  {label}
                </button>
              );
            })}
          </div>
        </Modal>
      )}
      {remove && (
        <Modal
          title="Xoá trang"
          className="sm"
          head={<SpaceTag space="note" />}
          onClose={() => {
            setRemove(false);
          }}
        >
          <h2>Bạn có chắc muốn xoá vĩnh viễn không?</h2>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
            “{body.title || "Trang này"}” sẽ bị xoá hẳn, không lấy lại được.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setRemove(false);
              }}
            >
              Huỷ
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                void write(`/v1/me/notes/${id}`, "DELETE", { rev: body.rev })
                  .then(async () => {
                    await store.forget(uid, id);
                    await client.invalidateQueries({ queryKey: ["notes", uid] });
                    setRemove(false);
                    if (close) close();
                    else
                      void navigate({
                        to: "/$",
                        params: { _splat: m === "quick" ? "notes" : `notebook/${m}` },
                      });
                  })
                  .catch(() => {});
              }}
            >
              🗑 Xoá vĩnh viễn
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

export function NotesPage({
  uid,
  path,
  notify,
  isAdmin,
}: {
  uid: string;
  path: string;
  notify: (s: string) => void;
  isAdmin: boolean;
}) {
  const navigate = useNavigate(),
    client = useQueryClient();
  const method =
    path.startsWith("/notebook/") && NOTE_METHODS.includes(path.split("/")[2] as NoteMethod)
      ? (path.split("/")[2] as NoteMethod)
      : undefined;
  const noteId = /^\/notes\/([\da-f-]{36})$/i.exec(path)?.[1];
  const [blogSlug, setBlogSlug] = useState(""),
    [blogBusy, setBlogBusy] = useState(false);
  const [q, setQ] = useState(""),
    [tag, setTag] = useState(""),
    [kind, setKind] = useState<Filter>("all"),
    [removing, setRemoving] = useState<Note | null>(null),
    [offset, setOffset] = useState(0),
    [drafts, setDrafts] = useState<NoteDraft[]>([]);
  const [created, setCreated] = useState<{ id: string; body: NoteInput; writer?: string } | null>(
      null,
    ),
    [review, setReview] = useState(false);
  useEffect(() => {
    const read = () => {
      void store.list(uid).then(setDrafts);
    };
    read();
    window.addEventListener("ca-note-synced", read);
    return () => {
      window.removeEventListener("ca-note-synced", read);
    };
  }, [uid]);
  useEffect(() => {
    setCreated(null);
    setOffset(0);
  }, [path]);
  const params = new URLSearchParams({ q, offset: String(offset), limit: "30" });
  const filterMethod =
    method ?? (kind === "quick" ? "quick" : kind === "self" ? "plain" : undefined);
  if (filterMethod) params.set("method", filterMethod);
  if (tag) params.set("tag", tag);
  if (kind === "pin") params.set("pinned", "1");
  const list = useQuery({
    queryKey: ["notes", uid, "list", params.toString()],
    enabled: !noteId,
    queryFn: async () => {
      try {
        const result = NoteList.parse(
          await api(`/v1/me/notes?${params.toString()}`, { accountId: uid }),
        );
        for (const item of result.items) await store.cache(uid, item);
        return result;
      } catch (e) {
        if (navigator.onLine) throw e;
        const all = (await store.cachedList(uid)).filter(
          (x) =>
            (!filterMethod || x.method === filterMethod) &&
            (!tag || x.tags.includes(tag)) &&
            (kind !== "pin" || !!x.pinnedAt) &&
            `${x.title} ${x.doc.html} ${x.doc.sections.join(" ")}`
              .toLowerCase()
              .includes(q.toLowerCase()),
        );
        return {
          items: all.slice(offset, offset + 30),
          total: all.length,
          limit: null,
          nextOffset: offset + 30 < all.length ? offset + 30 : null,
        };
      }
    },
  });
  const page = useQuery({
    queryKey: ["notes", uid, "page", noteId],
    enabled: !!noteId,
    queryFn: async () => {
      if (!noteId) throw new Error("Missing note");
      try {
        const note = Note.parse(await api(`/v1/me/notes/${noteId}`, { accountId: uid }));
        await store.cache(uid, note);
        return note;
      } catch (e) {
        if (navigator.onLine) throw e;
        const cached = await store.cached(uid, noteId);
        if (!cached) throw e;
        return cached;
      }
    },
  });
  const counts = [
      useNoteTotal(uid, "cornell"),
      useNoteTotal(uid, "box"),
      useNoteTotal(uid, "map"),
      useNoteTotal(uid, "mind"),
      useNoteTotal(uid, "plain"),
    ],
    due = useQuery({
      queryKey: ["notes", uid, "due"],
      queryFn: async () =>
        z.array(NoteReview).parse(await api("/v1/me/notes/due", { accountId: uid })),
    });
  if (review)
    return (
      <NoteRecall
        uid={uid}
        notify={notify}
        close={() => {
          setReview(false);
        }}
      />
    );
  if (created)
    return (
      <NoteEditor
        key={`${created.id}:${created.writer ?? "new"}`}
        writerToken={created.writer}
        uid={uid}
        id={created.id}
        initial={created.body}
        notify={notify}
        close={() => {
          setCreated(null);
        }}
        onNew={() => {
          setCreated({ id: uuidv7(), body: newInput(created.body.method) });
        }}
      />
    );
  if (noteId)
    return page.data ? (
      <NoteEditor
        key={noteId}
        uid={uid}
        id={noteId}
        initial={toInput(page.data)}
        pinnedAt={page.data.pinnedAt}
        notify={notify}
        onNew={() => {
          setCreated({ id: uuidv7(), body: newInput(page.data.method) });
        }}
      />
    ) : page.error ? (
      <ErrorState
        message={page.error.message}
        onRetry={() => {
          void page.refetch();
        }}
      />
    ) : (
      <Skeleton />
    );
  const shown = (list.data?.items ?? []).filter(
    (n) =>
      kind === "all" ||
      kind === "pin" ||
      (kind === "nb" && n.method !== "quick" && n.method !== "plain") ||
      (kind === "test" && n.source === "attempt") ||
      kind === "quick" ||
      kind === "self",
  );
  const total = list.data?.total ?? 0,
    limit = list.data?.limit ?? null;
  const head = method ? (
    <PageHead space="note" title={`${NB_ICON[method]} ${TITLES[method]}`} desc={NB_WHEN[method]}>
      <div className="row">
        <Button
          size="sm"
          onClick={() => {
            setCreated({ id: uuidv7(), body: newInput(method) });
          }}
        >
          ＋ Trang mới
        </Button>
        <Link to="/$" params={{ _splat: "notes" }} className="btn btn-soft btn-sm">
          ← Tất cả ghi chú
        </Link>
      </div>
    </PageHead>
  ) : (
    <PageHead
      space="note"
      title={
        <>
          Take note <NewBadge />
        </>
      }
      desc="Khu ghi chép riêng, đứng cạnh Làm bài và Năng suất. Chọn một phương pháp để mở trang mẫu, hoặc bấm 🗒️ Ghi nhanh trên thanh trên cùng ở bất kỳ trang nào. Mọi ghi chú, kể cả vở ghi lúc làm bài, gom về đây."
    />
  );
  return (
    <>
      {head}
      {!method && (
        <section className="stack" style={{ gap: 12 }}>
          <div className="nbsec-h">
            <h2>Phương pháp ghi chép</h2>
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
              mỗi phương pháp có trang mẫu sẵn · bảng, ảnh, sticker · 3 font · 4 loại giấy · 6 màu
              giấy
            </span>
          </div>
          <div className="nbm-grid">
            {BOOK_METHODS.map((m, i) => {
              const n = counts[i]?.data?.total ?? 0;
              return (
                <article
                  key={m}
                  className="nbm"
                  role="button"
                  tabIndex={0}
                  aria-label={`Mở ${TITLES[m]}`}
                  onClick={() => {
                    void navigate({ to: "/$", params: { _splat: `notebook/${m}` } });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target === e.currentTarget)
                      void navigate({ to: "/$", params: { _splat: `notebook/${m}` } });
                  }}
                >
                  <div
                    className={`nbm-th pc-${THUMB_BG[m]}`}
                    dangerouslySetInnerHTML={{ __html: NB_THUMB[m] ?? "" }}
                  />
                  <div className="row between">
                    <b>
                      {NB_ICON[m]} {m === "plain" ? TITLES.plain : LABELS[m]}
                    </b>
                    <Chip tone={n ? "acc" : undefined}>{n ? `${n} trang` : "chưa có trang"}</Chip>
                  </div>
                  <p>{NB_WHEN[m]}</p>
                  <Button
                    size="sm"
                    style={{ alignSelf: "flex-start" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreated({ id: uuidv7(), body: newInput(m) });
                    }}
                  >
                    ＋ Trang mới
                  </Button>
                </article>
              );
            })}
          </div>
        </section>
      )}
      <section className="card stack" style={{ gap: 12 }}>
        <div className="nbsec-h">
          <h2>{method ? `Các trang ${LABELS[method]}` : "Ghi chú của bạn"}</h2>
          <div className="row">
            <Chip tone={limit !== null && total >= limit ? "warn" : undefined}>
              {limit === null
                ? `${total} ghi chú · Pro`
                : `${total}/${limit} ghi chú · bản miễn phí`}
            </Chip>
            <Button
              size="sm"
              data-tip="Ôn ghi chú|Lặp lại ngắt quãng: nhớ rõ thì giãn lịch 1 → 3 → 7 → 14 → 30 ngày; quên thì ôn lại ngày mai"
              onClick={() => {
                setReview(true);
              }}
            >
              🔁 Ôn ghi chú{due.data?.length ? ` · ${due.data.length} đến hạn` : ""}
            </Button>
            <Button
              variant="soft"
              size="sm"
              onClick={() => window.dispatchEvent(new Event("ca-note-open"))}
            >
              🗒️ Ghi nhanh
            </Button>
            <Button
              variant="acc"
              size="sm"
              onClick={() => {
                setCreated({ id: uuidv7(), body: newInput(method ?? "plain") });
              }}
            >
              ＋ Ghi chú tự viết
            </Button>
          </div>
        </div>
        <div className="row between">
          {!method ? (
            <div className="row" style={{ gap: 6 }} role="tablist" aria-label="Lọc ghi chú">
              {FILTERS.map(([key, text]) => (
                <button
                  key={key}
                  className="tab"
                  role="tab"
                  aria-selected={kind === key}
                  onClick={() => {
                    setKind(key);
                    setOffset(0);
                  }}
                >
                  {text}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}
          <input
            className="input"
            style={{ width: 220 }}
            aria-label="Tìm trong ghi chú"
            placeholder="Tìm trong ghi chú…"
            value={q}
            onChange={(e) => {
              setQ(e.currentTarget.value);
              setOffset(0);
            }}
          />
        </div>
        <div className="tag-filter">
          <span className="tf-l">🏷 Nhãn</span>
          {NB_TAGS.map(([label, color]) => (
            <button
              key={label}
              className={`tag-f${tag === label ? " on" : ""}`}
              style={{ "--tc": color } as CSSProperties}
              aria-pressed={tag === label}
              onClick={() => {
                setTag(tag === label ? "" : label);
                setOffset(0);
              }}
            >
              <i />
              {label}
            </button>
          ))}
          {tag && (
            <button
              className="tag-f clear"
              onClick={() => {
                setTag("");
              }}
            >
              ✕ Bỏ lọc
            </button>
          )}
        </div>
        {isAdmin && !method && (
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              if (!blogSlug.trim() || blogBusy) return;
              setBlogBusy(true);
              void actions
                .create(uid, `blog:${blogSlug.trim()}`, (id) => ({
                  path: `/v1/me/notes/blog/${id}`,
                  body: { slug: blogSlug.trim() },
                }))
                .then((value) => Note.parse(value))
                .then(async (note) => {
                  await store.cache(uid, note);
                  await navigate({ to: "/$", params: { _splat: `notes/${note.id}` } });
                })
                .catch((e: unknown) => {
                  notify(e instanceof Error ? e.message : "Chưa lưu được bài blog.");
                })
                .finally(() => {
                  setBlogBusy(false);
                });
            }}
          >
            {/* Chỉ tài khoản quản trị (QUYET-DINH S13). */}
            <Chip tone="dark">Admin</Chip>
            <input
              className="input"
              style={{ width: 260 }}
              aria-label="Slug bài blog"
              placeholder="Slug bài blog đã xuất bản"
              value={blogSlug}
              onChange={(e) => {
                setBlogSlug(e.currentTarget.value);
              }}
            />
            <Button type="submit" variant="soft" size="sm" disabled={blogBusy || !blogSlug.trim()}>
              Lưu bài blog
            </Button>
          </form>
        )}
      </section>
      {drafts.some((d) => d.dirty) && (
        <section className="resume">
          <b>Bản nháp trên máy, chưa đồng bộ</b>
          <div className="row">
            {drafts
              .filter((d) => d.dirty)
              .map((d) => (
                <Button
                  key={d.key}
                  variant="soft"
                  size="sm"
                  onClick={() => {
                    setCreated({ id: d.id, body: d.body, writer: d.writer });
                  }}
                >
                  {d.body.title || "Chưa đặt tên"} · {d.blocked ? "Cần xử lý" : "Chưa đồng bộ"}
                </Button>
              ))}
          </div>
        </section>
      )}
      {list.error && (
        <ErrorState
          message={list.error.message}
          onRetry={() => {
            void list.refetch();
          }}
        />
      )}
      {list.isPending && <Skeleton />}
      {shown.length ? (
        <div className="grid g3">
          {shown.map((note) => {
            const title = note.title || "Chưa đặt tên";
            const open = () => {
              void navigate({ to: "/$", params: { _splat: `notes/${note.id}` } });
            };
            return (
              <article
                key={note.id}
                className={`note${note.pinnedAt ? " pinned" : ""}`}
                data-act="note-open"
                onClick={open}
              >
                <div className="nt">
                  <button
                    className="note-t"
                    title={title}
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                  >
                    {title}
                  </button>
                  <span className="nd">{shortDate(note.updatedAt)}</span>
                  <span className="note-acts">
                    <button
                      className={`note-ib pin${note.pinnedAt ? " on" : ""}`}
                      aria-pressed={!!note.pinnedAt}
                      aria-label={`${note.pinnedAt ? "Bỏ ghim" : "Ghim"}: ${title}`}
                      data-tip={note.pinnedAt ? "Bỏ ghim" : "Ghim lên đầu"}
                      onClick={(e) => {
                        e.stopPropagation();
                        void setPin(uid, note.id, !note.pinnedAt)
                          .then(() => client.invalidateQueries({ queryKey: ["notes", uid] }))
                          .catch((err: unknown) => {
                            notify(err instanceof Error ? err.message : "Chưa ghim được.");
                          });
                      }}
                    >
                      📌
                    </button>
                    <button
                      className="note-ib del"
                      aria-label={`Xoá: ${title}`}
                      data-tip="Xoá ghi chú|Sẽ hỏi lại trước khi xoá vĩnh viễn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRemoving(note);
                      }}
                    >
                      🗑
                    </button>
                  </span>
                </div>
                <div className="note-meta">
                  {note.source === "attempt" ? (
                    <Chip tone="acc">📝 ghi lúc làm bài · mở câu gốc →</Chip>
                  ) : note.method === "quick" ? (
                    <Chip tone="warn">⚡ ghi nhanh</Chip>
                  ) : (
                    <Chip tone="acc">
                      {NB_ICON[note.method]} {LABELS[note.method]}
                    </Chip>
                  )}
                  {note.tags.map((t) => {
                    const color = NB_TAGS.find(([label]) => label === t)?.[1];
                    return (
                      <span
                        key={t}
                        className="tag-chip"
                        style={{ "--tc": color ?? "var(--ink-3)" } as CSSProperties}
                      >
                        {t}
                      </span>
                    );
                  })}
                </div>
                <div
                  className={`paper pc-${PAPER_TONE[note.color]}`}
                  style={{ background: "var(--paper)" }}
                >
                  {excerpt(note) || <i>Trang có bảng, ảnh hoặc sticker</i>}
                </div>
              </article>
            );
          })}
        </div>
      ) : list.data ? (
        <section className="card faint">
          {kind === "pin" && !q && !tag
            ? "Chưa ghim ghi chú nào. Bấm 📌 trên một ghi chú để ghim."
            : total === 0 && !q && !tag && kind === "all"
              ? "Chưa có ghi chú. Bấm 🗒️ Ghi nhanh ở bất kỳ trang nào."
              : "Không có ghi chú nào khớp."}
        </section>
      ) : null}
      {(offset > 0 || list.data?.nextOffset != null) && (
        <div className="row">
          <Button
            variant="soft"
            size="sm"
            disabled={offset === 0}
            onClick={() => {
              setOffset(Math.max(0, offset - 30));
            }}
          >
            ‹ Trang trước
          </Button>
          <Button
            variant="soft"
            size="sm"
            disabled={list.data?.nextOffset == null}
            onClick={() => {
              setOffset(list.data?.nextOffset ?? 0);
            }}
          >
            Trang sau ›
          </Button>
        </div>
      )}
      <p className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
        ⏺ Xem danh sách chưa tính giờ; mở một ghi chú hay một trang sổ và bắt đầu viết thì mới tính
        (nhóm “Khác”).
      </p>
      {removing && (
        <Modal
          title="Xoá ghi chú"
          className="sm"
          head={<SpaceTag space="note" />}
          onClose={() => {
            setRemoving(null);
          }}
        >
          <h2>Bạn có chắc muốn xoá vĩnh viễn không?</h2>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
            “{removing.title || "Chưa đặt tên"}” sẽ bị xoá hẳn, không lấy lại được.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setRemoving(null);
              }}
            >
              Huỷ
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const n = removing;
                void api(`/v1/me/notes/${n.id}`, {
                  method: "DELETE",
                  accountId: uid,
                  key: uuidv7(),
                  body: { rev: n.rev },
                })
                  .then(async () => {
                    setRemoving(null);
                    notify("Đã xoá ghi chú.");
                    await client.invalidateQueries({ queryKey: ["notes", uid] });
                  })
                  .catch((e: unknown) => {
                    notify(e instanceof Error ? e.message : "Chưa xoá được.");
                  });
              }}
            >
              🗑 Xoá vĩnh viễn
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

function NoteRecall({
  uid,
  notify,
  close,
}: {
  uid: string;
  notify: (s: string) => void;
  close: () => void;
}) {
  const query = useQuery({
    queryKey: ["notes", uid, "due"],
    queryFn: async () =>
      z.array(NoteReview).parse(await api("/v1/me/notes/due", { accountId: uid })),
  });
  const [reveal, setReveal] = useState(false),
    [blank, setBlank] = useState(false),
    [answer, setAnswer] = useState(""),
    [busy, setBusy] = useState(false);
  const write = useWrite(uid, notify),
    card = query.data?.[0];
  useRevisionStudy(uid, "note", card?.note.id, noteActivity(card?.note.context ?? null));
  return (
    <>
      <PageHead
        space="note"
        title="🔁 Ôn ghi chú"
        desc="Lặp lại ngắt quãng: nhớ rõ thì giãn lịch 1 → 3 → 7 → 14 → 30 ngày; quên thì ôn lại sau 15 phút."
      >
        <div className="row">
          <Button variant="soft" size="sm" onClick={close}>
            ← Tất cả ghi chú
          </Button>
          <span className="row" style={{ gap: 8, fontSize: 13.5, fontWeight: 650 }}>
            Trang trắng
            <Switch label="Trang trắng" checked={blank} onChange={setBlank} />
          </span>
        </div>
      </PageHead>
      {query.isPending ? (
        <Skeleton />
      ) : query.error ? (
        <ErrorState
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : !card ? (
        <section className="card">
          <div className="flash">
            <div style={{ width: 110 }}>
              <Fish pose="party" />
            </div>
            <b style={{ fontSize: 18 }}>Chưa có ghi chú đến hạn</b>
            <p className="muted">
              Trang mới sẽ ôn nhanh sau 15 phút, rồi 1 → 3 → 7 → 14 → 30 ngày.
            </p>
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="rv-top">
            <span className="chip acc">{LABELS[card.note.method]}</span>
            <span className="rv-prog">
              <i style={{ width: "0%" }} />
            </span>
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
              {query.data.length} trang đến hạn
            </span>
          </div>
          <div className="rv-card">
            <div className="rv-t">{card.note.title || "Chưa đặt tên"}</div>
            {!reveal && !blank && card.note.method === "cornell" && (
              <div
                className="rv-cue"
                dangerouslySetInnerHTML={{
                  __html: sanitizeLocalNote(card.note.doc.sections[0] ?? ""),
                }}
              />
            )}
            {!reveal && (
              <>
                <p className="rv-ask">
                  {blank
                    ? "Viết lại những gì bạn nhớ trên trang trắng."
                    : card.note.method === "cornell"
                      ? "Trả lời từ cột gợi ý trước khi mở ghi chép."
                      : "Nhắc lại các ý chính của trang này."}
                </p>
                <textarea
                  className="input rv-recall"
                  aria-label="Câu trả lời ôn ghi chú"
                  value={answer}
                  onChange={(e) => {
                    setAnswer(e.currentTarget.value);
                  }}
                />
                <Button
                  className="rv-flip"
                  onClick={() => {
                    setReveal(true);
                  }}
                >
                  Xem ghi chú
                </Button>
              </>
            )}
            {reveal && (
              <>
                <div
                  className="rv-cue"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeLocalNote(
                      [card.note.doc.html, ...card.note.doc.sections].filter(Boolean).join("<hr>"),
                    ),
                  }}
                />
                {card.note.doc.assets.map((id) => (
                  <PrivateImage key={id} uid={uid} id={id} />
                ))}
                <div className="rv-rate">
                  {(
                    [
                      ["😵 Chưa nhớ", "ôn lại sau 15 phút"],
                      ["🤔 Khó", "giữ nhịp hiện tại"],
                      ["😎 Nhớ", "giãn lịch ôn"],
                    ] as const
                  ).map(([label, sub], grade) => (
                    <button
                      key={label}
                      className={`btn rv-b${grade}`}
                      disabled={busy}
                      aria-label={["Chưa nhớ · 15 phút", "Khó", "Nhớ"][grade]}
                      onClick={() => {
                        setBusy(true);
                        void write(`/v1/me/notes/reviews/${card.id}`, "POST", {
                          grade,
                          lastReviewedAt: card.lastReviewedAt,
                        })
                          .then(async () => {
                            await query.refetch();
                            setReveal(false);
                            setAnswer("");
                          })
                          .catch(() => {})
                          .finally(() => {
                            setBusy(false);
                          });
                      }}
                    >
                      {label}
                      <small>{sub}</small>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
}
